import restClient from 'Shared/hooks/restClient'

const VerifyTwoFactor = ({ 
  tooManyAttempts, 
  channel: initialChannel, 
  maskedPhoneNumber, 
  error
}) => {
  const [inputs, setInputs] = useState(Array(6).fill(''))
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(error ? "That's not the right code. Please try again." : '')
  const [channel, setChannel] = useState(initialChannel)
  const wrapperRef = useRef(null)
  const inputRefs = useRef([])
  const code = inputs.join('')

  useEffect(() => {
    if (channel && inputRefs.current[0]) {
      inputRefs.current[0].focus()
      inputRefs.current[0].select()
    }
  }, [channel])

  const focusElement = (elem) => {
    if (elem) {
      elem.focus()
      // Don't select on focus, let natural cursor position work
    }
  }

  const handleInput = (index, value) => {
    if (value !== '' && !/^\d$/.test(value)) {
      return
    }

    const newInputs = [...inputs]

    if (value === '') {
      newInputs[index] = ''
    } else {
      newInputs[index] = value

      // Auto-advance to next input
      if (index < 5) {
        setTimeout(() => focusElement(inputRefs.current[index + 1]), 0)
      }
    }

    setInputs(newInputs)
  }

  const handleKeyDown = (index, e) => {
    const newInputs = [...inputs]

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        if (index < 5) {
          focusElement(inputRefs.current[index + 1])
        }
        break
      case 'ArrowLeft':
        e.preventDefault()
        if (index > 0) {
          focusElement(inputRefs.current[index - 1])
        }
        break
      case 'Backspace':
        e.preventDefault()
        if (inputs[index] !== '') {
          newInputs[index] = ''
          setInputs(newInputs)
        } else if (index > 0) {
          newInputs[index - 1] = ''
          setInputs(newInputs)
          setTimeout(() => focusElement(inputRefs.current[index - 1]), 0)
        }
        break
      case 'Delete':
        e.preventDefault()
        newInputs[index] = ''
        setInputs(newInputs)
        break
      case 'Enter':
        e.preventDefault()
        if (code.length === 6) {
          handleSubmit()
        }
        break
      case 'v':
        if (e.ctrlKey || e.metaKey) {
          return
        }
        break
    }
  }

  const handlePaste = (index, e) => {
    e.preventDefault()
    const paste = e.clipboardData.getData('text')
    const digits = paste.replace(/\D/g, '').slice(0, 6)

    if (digits.length > 0) {
      const newInputs = [...inputs]
      const startIndex = index

      for (let i = 0; i < digits.length && startIndex + i < 6; i++) {
        newInputs[startIndex + i] = digits[i]
      }

      setInputs(newInputs)

      // Focus the input after the last pasted digit, or the last input
      const nextFocusIndex = Math.min(startIndex + digits.length, 5);
      setTimeout(() => focusElement(inputRefs.current[nextFocusIndex]), 0);
    }
  }

  const handleClick = (index) => {
    focusElement(inputRefs.current[index])
  }

  const handleSubmit = () => {
    if (isLoading || code.length !== 6) return

    setIsLoading(true)
    setErrorMessage('')

    restClient.post('/api/user/auth/verify', { code, channel })
      .then(response => {
        if (response.data.success) {
          window.location.href = response.data.redirectUrl
        } else {
          setErrorMessage(response.data.message)
          setInputs(Array(6).fill(''))
          setTimeout(() => focusElement(inputRefs.current[0]), 0)
        }
      })
      .catch(error => {
        setErrorMessage('Something went wrong. Please try again.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleSendCode = () => {
    if (isLoading) return

    setIsLoading(true)
    setErrorMessage('')

    restClient.post('/api/user/auth/verify/send_code')
      .then(response => {
        if (response.data.success) {
          setChannel(response.data.channel)
          setErrorMessage('')
          setTimeout(() => {
            if (inputRefs.current[0]) {
              inputRefs.current[0].focus()
            }
          }, 100)
        } else {
          if (response.data.error === 'too_many_attempts') {
            setErrorMessage('Too many attempts. Please try again later.')
          } else {
            setErrorMessage(response.data.message)
          }
        }
      })
      .catch(error => {
        setErrorMessage('Something went wrong. Please try again.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const renderCodeInputs = () => {
    return (
      <div id="two-factor-code-wrapper">
        <div 
          className="code__digits" 
          style={{ '--total-digits': 6 }}
          ref={wrapperRef}
        >
          {inputs.map((value, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              className={`code__digit ${errorMessage && value === '' ? 'invalid' : ''}`}
              type="text"
              value={value}
              onChange={(e) => handleInput(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              onClick={() => handleClick(index)}
              onFocus={(e) => e.target.select()}
              aria-label={`Digit ${index + 1}`}
              aria-required="true"
              autoComplete="one-time-code"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              maxLength="1"
              inputMode="numeric"
              pattern="[0-9]*"
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="container" id="verify-account">
      <div className="box">
        <div className="icon">
          <SvgIconsConfirmAccount />
        </div>

        <h1>Confirm your account</h1>

        {tooManyAttempts ? (
          <h2 className="no-margin-top">
            Your account is temporarily locked, due to too many login attempts. Please try again in an hour.
          </h2>
        ) : channel ? (
          <>
            {channel === 'sms' && (
              <p>We have sent a one-time code to {maskedPhoneNumber}</p>
            )}
            
            {channel === 'call' && (
              <>
                <h2 className="no-margin-top">We're calling you on {maskedPhoneNumber}</h2>
                <p>
                  You'll be asked to press a button on your keypad in the first ten seconds. 
                  You'll then hear a code you need to enter below.
                </p>
              </>
            )}

            <div className="field">
              <p>Enter the code here to confirm your account.</p>
              
              {renderCodeInputs()}
              
              {errorMessage && (
                <p className="error">{errorMessage}</p>
              )}

              <p>Your code will expire in 10 minutes</p>
            </div>

            <button 
              type="button"
              onClick={handleSubmit}
              className="button primary large"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Continue'}
            </button>

            <p className="margin-top no-margin-bottom">
              <a href="/users/sign_out" className="blue-link">
                Back
              </a>
            </p>
          </>
        ) : (
          <>
            {errorMessage && (
              <p className="error">{errorMessage}</p>
            )}
            <p>We'll send a one-time code to your phone, which you'll need to continue logging in.</p>
            <button 
              type="button"
              onClick={handleSendCode}
              className="button large primary"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send me a code'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default VerifyTwoFactor
